// src/contexts/AuthContext.tsx
import React, { createContext, useState, useContext, ReactNode, useEffect, useCallback } from 'react';
import { TeamMember } from '../components/settings/interface';
import { getUser, completeUserOnboarding } from '../services/userService';
import { getStaticData } from '../services/dashboardService';
import { useNavigate, useLocation } from 'react-router-dom';
import { OnboardingFormData } from '@/components/onboarding/types';

interface AuthContextType {
    isAuthenticated: boolean;
    user: TeamMember | null;
    setLogin: (userToken: string) => Promise<void>;
    doLogout: () => void;
    fetchUser: () => void;
    isLoading: boolean;
    staticData: any;
    isTeamMember: boolean;
    setIsTeamMember: (teamMember: boolean) => void;
    completeOnboarding: (data: OnboardingFormData) => Promise<boolean>;
    refreshStaticData: () => Promise<void>;
}

const PUBLIC_ROUTES = [
    '/auth',
    '/network-error',
    '/github/callback',
    '/forgot-password',
    '/payment-success',
    '/payment-cancelled'
];

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [token, setToken] = useState<string | null>(null);
    const [user, setUser] = useState<TeamMember | null>(null);
    const [staticData, setStaticData] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();
    const location = useLocation();
    const [isTeamMember, setIsTeamMember] = useState(false);

    const pathname = location.pathname;
    const isOnPublicRoute = PUBLIC_ROUTES.includes(pathname);

    // Define doLogout first as it's used by fetchUser
    const doLogout = useCallback(() => {
        localStorage.removeItem('authToken');
        sessionStorage.clear();
        setToken(null);
        setUser(null);
        setStaticData(null);
        navigate('/auth');
    }, [navigate]);

    const fetchStaticData = useCallback(async () => {
        try {
            const { success, data } = await getStaticData();
            if (success) {
                setStaticData(data);
            } else {
                console.warn('Failed to fetch static data');
            }
        } catch (error) {
            console.error('Error fetching static data:', error);
        }
    }, []);

    const fetchUser = useCallback(async () => {
        try {
            const { success, data } = await getUser();
            if (success) {
                setUser(data);
                return data;
            } else {
                doLogout();
                return null;
            }
        } catch (error: any) {
            if (!error.response) {
                if (pathname !== '/network-error') {
                    navigate('/network-error');
                }
            } else {
                console.error('Error fetching user data:', error);
            }
            return null;
        }
    }, [navigate, pathname, doLogout]);

    // Re-fetch static data when selected project changes
    // This ensures permissions are scoped to the selected project
    const refreshStaticData = useCallback(async () => {
        await fetchStaticData();
    }, [fetchStaticData]);

    const completeOnboarding = async (data: OnboardingFormData) => {
        const { success } = await completeUserOnboarding(data);
        if (success) {
            return true;
        } else {
           return false;
        }
    }

    // Initialize auth on mount and pathname changes
    useEffect(() => {
        const initializeAuth = async () => {
            const authToken = localStorage.getItem('authToken');
            if (authToken && pathname !== '/network-error') {
                setToken(authToken);
                setIsLoading(true);
                const userData = await fetchUser();
                await fetchStaticData();
                setIsLoading(false);
                
                // Use userData instead of user state
                if (userData && !userData.accountInfo?.onboardingCompleted && pathname !== '/onboarding') {
                    navigate('/onboarding');
                }
            } else if (!authToken && !isOnPublicRoute) {
                navigate('/auth');
                setIsLoading(false);
            } else {
                setIsLoading(false);
            }
        };
        initializeAuth();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate, pathname, isOnPublicRoute]);

    // Listen for subscription changes and auto-refresh user data
    // This ensures plan updates reflect immediately without requiring logout
    useEffect(() => {
        const handleSubscriptionUpdate = () => {
            fetchUser();
        };
        
        window.addEventListener('subscription:updated', handleSubscriptionUpdate);
        return () => window.removeEventListener('subscription:updated', handleSubscriptionUpdate);
    }, [fetchUser]);

    const setLogin = useCallback(async (authToken: string) => {
        localStorage.setItem('authToken', authToken);
        setToken(authToken);
        const userData = await fetchUser();
        await fetchStaticData();
        
        if (userData && !userData.accountInfo?.onboardingCompleted) {
            navigate('/onboarding');
        }
    }, [fetchUser, fetchStaticData, navigate]);

    const isAuthenticated = !!token;

    return (
        <AuthContext.Provider value={{ isAuthenticated, user, setLogin, doLogout, isLoading, staticData, fetchUser, isTeamMember, setIsTeamMember, completeOnboarding, refreshStaticData }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export default AuthContext;